import React, { useState, useRef, useEffect, useMemo, memo } from 'react';
import Navbar from './Navbar';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitText from './SplitText';
import Transition from './Transition';
import SEO from './SEO';
import Cal, { getCalApi } from '@calcom/embed-react';
import {
  getServiceBySlug,
  getTierByName,
  getAddonByLabel,
  computeQuoteEstimate,
  formatEuro,
  resolveServiceSlug,
} from '../data/services';
import { postContact, CONTACT_ENDPOINT } from '../lib/contactClient';

// Update once Sander has set up his Cal.com event slug.
const CAL_LINK = 'skdesign/discovery-call';

gsap.registerPlugin(ScrollTrigger);

const AVAILABLE_SERVICES = ['Web design', 'Motion design', 'Branding', 'Logo design', 'Other'];
const REFERRAL_SOURCES = ['LinkedIn', 'ArtStation', 'Instagram', 'Referral', 'Search', 'Other'];

// Memoized component for contact header to prevent re-animation
const ContactHeader = memo(() => (
  <div className="flex flex-col gap-4 text-[#262624]">
    <SplitText
      text="CONTACT"
      className="font-anton uppercase leading-[0.9] text-[16vw] tracking-[-4px] sm:text-[14vw] sm:tracking-[-5px] md:text-[12vw] md:tracking-[-6px] lg:text-[200px] lg:tracking-[-8px]"
      splitType="chars"
      delay={50}
      duration={0.8}
      ease="power3.out"
      from={{ opacity: 0, y: 100 }}
      to={{ opacity: 1, y: 0 }}
      threshold={0}
      rootMargin="+100px"
    />
    <SplitText
      text="Let's start with a quick hello to talk through your project — goals, scope, timeline. I'll answer anything about my process, and we'll map out the next steps. Looking forward to connecting!"
      className="font-bricolage text-[18px] leading-[1.35] tracking-[-0.4px] sm:text-[20px] sm:leading-[1.3] md:text-[22px] lg:text-[30px] lg:tracking-[-1.2px] lg:leading-[1.05]"
      splitType="lines"
      delay={30}
      duration={0.6}
      ease="power3.out"
      from={{ opacity: 0, y: 20 }}
      to={{ opacity: 1, y: 0 }}
      threshold={0}
      rootMargin="+100px"
    />
  </div>
));

ContactHeader.displayName = 'ContactHeader';

// Two-tab segmented control + body. Lets the visitor pick between sending
// the form on its own (Email) or booking a discovery call via Cal.com.
const ContactPreference = ({ mode, onChange, variant = 'desktop' }) => {
  const isDesktop = variant === 'desktop';
  const tabBase = 'flex-1 rounded-full px-4 py-2.5 font-bricolage font-extrabold uppercase text-[12px] sm:text-[13px] tracking-[0.04em] transition-all duration-200 cursor-pointer';
  const tabActive = 'bg-[#fe9043] text-[#262624]';
  const tabIdle = 'bg-transparent text-[#f2f3d9]/65 hover:text-[#f2f3d9]';

  const Tabs = (
    <div role="tablist" aria-label="How would you like to get in touch?" className="flex gap-1 p-1 rounded-full bg-[#f2f3d9]/8 border border-[#f2f3d9]/15">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'email'}
        onClick={() => onChange('email')}
        className={`${tabBase} ${mode === 'email' ? tabActive : tabIdle}`}
      >
        Email me back
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'call'}
        onClick={() => onChange('call')}
        className={`${tabBase} ${mode === 'call' ? tabActive : tabIdle}`}
      >
        Book a call
      </button>
    </div>
  );

  // Shown until the visitor actively picks email or call — the choice is
  // required before the form can be submitted.
  const PromptBody = (
    <div className="flex items-start gap-3 px-1 py-3">
      <svg className="w-5 h-5 text-[#fe9043] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p className="font-space text-[#f2f3d9]/70 text-[14px] sm:text-[15px] leading-[1.5]">
        Pick how I should get back to you — a written reply by email, or a quick discovery call. Required before sending.
      </p>
    </div>
  );

  const EmailBody = (
    <div className="flex flex-col gap-4 px-1 py-3">
      <div className="font-bricolage font-extrabold text-[#f2f3d9] text-[24px] sm:text-[28px] leading-[1.05] tracking-tight">
        I'll reply by email within 24 working hours.
      </div>
      <p className="font-space text-[#f2f3d9]/65 text-[14px] sm:text-[15px] leading-[1.5]">
        Just fill in the form above — package, scope, deadline. You'll get a tailored quote and next steps in your inbox.
      </p>
      <div className="flex items-center gap-2 font-space text-[#fe9043] text-[12px] uppercase tracking-[0.18em]">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <span>No call required</span>
      </div>
      <button
        type="button"
        onClick={() => onChange('call')}
        className="self-start font-space text-[#f2f3d9]/55 hover:text-[#f2f3d9] text-[13px] underline underline-offset-4 decoration-[#f2f3d9]/30 hover:decoration-[#fe9043] transition-colors duration-200"
      >
        Prefer to talk it through? Book a call instead →
      </button>
    </div>
  );

  const CallBody = (
    <div className="flex flex-col gap-3">
      {/* Timezone / availability context (Fase 7) so visitors know what they're
          booking and in which timezone before they pick a slot. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-space text-[12px] text-[#f2f3d9]/70">
        <span className="inline-flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-[#fe9043]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
          </svg>
          <span>Belgium · CET/CEST</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-[#fe9043]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span>30-min discovery call</span>
        </span>
      </div>
      <div className="rounded-[12px] overflow-hidden bg-[#f2f3d9]">
        <Cal
          key={`cal-${variant}`}
          namespace={`discovery-${variant}`}
          calLink={CAL_LINK}
          style={{ width: '100%', height: isDesktop ? '560px' : '520px', overflow: 'scroll' }}
          config={{ layout: 'month_view' }}
        />
      </div>
      <span className="font-space text-[#f2f3d9]/55 text-[12px] leading-[1.4]">
        Pick a slot that works for you — confirmation lands in your inbox right after.
      </span>
    </div>
  );

  return (
    <div className="bg-[#262624] rounded-[20px] w-full max-w-[599px] mx-auto p-5 flex flex-col gap-4">
      <span className="font-space text-[#f2f3d9]/55 text-[11px] uppercase tracking-[0.22em]">
        How should I get back to you? <span className="text-[#fe9043]">*</span>
      </span>
      {Tabs}
      {mode === 'email' ? EmailBody : mode === 'call' ? CallBody : PromptBody}
    </div>
  );
};

// Inline tier picker shown right under the service grid when the visitor has
// chip-selected exactly one service and hasn't arrived via /contact?tier=...
// Mirrors the /services-page tier cards but compact, so people can scope a
// package without round-tripping to /services first.
const TierPickerInline = ({
  service,
  onSelect,
  selectedTierName = null,
  onClear,
  showServiceLabel = false,
}) => {
  if (!service?.tiers?.length) return null;
  return (
    <div className="mt-8 sm:mt-10 rounded-2xl bg-[#262624] text-[#f2f3d9] p-5 sm:p-6 lg:p-7">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <span className="font-space text-[#f2f3d9]/55 text-[10px] sm:text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2">
          {showServiceLabel && <span className="text-base leading-none not-italic">{service.icon}</span>}
          {showServiceLabel ? `${service.title} package` : 'Pick a package'} <span className="opacity-60">(optional)</span>
        </span>
        <span className="font-space text-[#f2f3d9]/45 text-[10px] sm:text-xs uppercase tracking-[0.18em]">
          all prices excl. 21% VAT
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
        {service.tiers.map((tier) => {
          const isActive = selectedTierName === tier.name;
          return (
            <button
              key={tier.name}
              type="button"
              onClick={() => (isActive ? onClear?.() : onSelect(tier))}
              aria-pressed={isActive}
              className={`text-left rounded-xl border p-4 transition-all duration-200 ${
                isActive
                  ? 'bg-[#fe9043] text-[#262624] border-[#fe9043]'
                  : tier.featured
                    ? 'bg-[#f2f3d9]/10 text-[#f2f3d9] border-[#fe9043]/60 hover:border-[#fe9043]'
                    : 'bg-[#f2f3d9]/5 text-[#f2f3d9] border-[#f2f3d9]/20 hover:border-[#f2f3d9]/60'
              }`}
            >
              <div className="flex items-baseline justify-between gap-2 mb-1.5">
                <span className="font-bricolage font-extrabold text-base sm:text-lg uppercase tracking-tight">
                  {tier.name}
                </span>
                <span
                  className={`font-space text-[10px] uppercase tracking-[0.18em] ${
                    isActive ? 'text-[#262624]/65' : 'text-[#f2f3d9]/50'
                  }`}
                >
                  {tier.timeline}
                </span>
              </div>
              <div className="font-bricolage font-extrabold text-lg sm:text-xl tracking-tight">
                {tier.price}
              </div>
              <p
                className={`mt-1.5 font-space text-[12px] sm:text-[13px] leading-snug ${
                  isActive ? 'text-[#262624]/70' : 'text-[#f2f3d9]/60'
                }`}
              >
                {tier.bestFor}
              </p>
              {isActive && (
                <span className="mt-2 inline-flex items-center gap-1 font-space text-[10px] uppercase tracking-[0.18em] text-[#262624]/70">
                  Selected · tap to remove
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-4 font-space text-[#f2f3d9]/55 text-[12px] sm:text-[13px] leading-snug">
        Not sure yet? Skip this — combine packages from multiple services or just describe your project in the message field, and I'll come back with a tailored proposal.
      </p>
    </div>
  );
};

// Subtle progress indicator (Fase 7): You → Project → Details → Send. Reflects
// how far the visitor has filled the form so the (long) form feels guided
// rather than endless. Purely a visual aid — submission is still gated by the
// form's own required fields + terms checkbox.
const ContactProgress = ({ steps }) => {
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <div className="mb-10 rounded-2xl border border-[#262624]/15 bg-white/40 p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="font-space text-[#262624]/55 text-[10px] sm:text-xs uppercase tracking-[0.22em]">
          Your request
        </span>
        <span className="font-space text-[#262624]/55 text-[10px] sm:text-xs uppercase tracking-[0.18em] tabular-nums">
          {pct}% complete
        </span>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {steps.map((step, i) => (
          <React.Fragment key={step.label}>
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full flex-shrink-0 transition-all duration-300 ${
                  step.done
                    ? 'bg-[#262624] text-[#f2f3d9]'
                    : 'border-2 border-[#262624]/30 text-[#262624]/40'
                }`}
              >
                {step.done ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span className="font-space text-[11px] sm:text-xs font-bold">{i + 1}</span>
                )}
              </span>
              <span
                className={`font-space text-[11px] sm:text-[13px] uppercase tracking-[0.1em] truncate transition-colors duration-300 ${
                  step.done ? 'text-[#262624]' : 'text-[#262624]/45'
                }`}
              >
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <span
                className={`hidden sm:block h-px flex-1 min-w-[10px] transition-colors duration-300 ${
                  step.done ? 'bg-[#262624]/40' : 'bg-[#262624]/12'
                }`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// "What happens next" (Fase 7) — sets expectations and lowers friction by
// showing the path from submit to kickoff right next to the form.
const NEXT_STEPS = [
  { no: '01', title: 'Reply', body: 'A personal email back within 24 working hours.' },
  { no: '02', title: 'Scope', body: 'We align on goals, deliverables and timeline.' },
  { no: '03', title: 'Proposal', body: 'A fixed, tailored quote — no surprises.' },
  { no: '04', title: 'Kickoff', body: 'Deposit, shared board, and we start building.' },
];

const WhatHappensNext = () => (
  <div className="rounded-[20px] border border-[#262624]/15 bg-white/40 p-5 sm:p-6">
    <span className="font-space text-[#262624]/55 text-[11px] uppercase tracking-[0.22em]">
      What happens next
    </span>
    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
      {NEXT_STEPS.map((step) => (
        <div key={step.no} className="flex flex-col gap-1.5">
          <span className="font-anton text-[#f97b22] text-[28px] sm:text-[32px] leading-none">
            {step.no}
          </span>
          <h4 className="font-bricolage font-extrabold text-[#262624] text-[16px] sm:text-[17px]">
            {step.title}
          </h4>
          <p className="font-space text-[#262624]/65 text-[13px] sm:text-[14px] leading-[1.45]">
            {step.body}
          </p>
        </div>
      ))}
    </div>
  </div>
);

const ContactSection = () => {
  const location = useLocation();
  // null    = no choice made yet — required before the form can be submitted,
  // 'email' = visitor just wants a written reply (form is enough),
  // 'call'  = visitor wants to book a discovery call via Cal.com.
  const [contactMode, setContactMode] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  // Map of service slug -> { service, tier }. Lets a visitor combine packages
  // across services (e.g. Branding + Web design) and have prices summed.
  const [selectedTiers, setSelectedTiers] = useState({});
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [hasMessage, setHasMessage] = useState(false);
  const footerContainerRef = useRef(null);
  const formRef = useRef(null);

  // Add-ons grouped per selected service, so when a visitor combines e.g.
  // Branding + Motion design they see two clearly-labelled blocks instead of
  // one flat list. Selection state stays label-keyed (URL prefill + email
  // payload backwards compatible) — addon labels don't collide across
  // services in our current catalogue.
  const groupedAddons = useMemo(() => {
    return selectedServices
      .map((slug) => {
        const svc = getServiceBySlug(slug);
        return {
          slug,
          service: svc,
          addons: svc?.addons ?? [],
        };
      })
      .filter((g) => g.addons.length > 0);
  }, [selectedServices]);

  const availableAddonLabels = useMemo(() => {
    const labels = new Set();
    groupedAddons.forEach((g) => g.addons.forEach((a) => labels.add(a.label)));
    return labels;
  }, [groupedAddons]);

  // Flat list of the chosen { service, tier } pairs + a quick "any package?" flag.
  const selectedTierList = useMemo(() => Object.values(selectedTiers), [selectedTiers]);
  const hasAnyTier = selectedTierList.length > 0;

  const setTierForService = (service, tier) => {
    setSelectedTiers((prev) => ({ ...prev, [service.slug]: { service, tier } }));
  };

  const removeTierForService = (slug) => {
    setSelectedTiers((prev) => {
      if (!prev[slug]) return prev;
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  };

  // Prefill from ?service= / ?tier= / ?addon= query params (used by Services CTAs)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Map any legacy slugs (e.g. "3D / VFX") onto current ones before matching
    // so older shared/email links keep prefilling instead of silently no-op'ing.
    const requested = params.getAll('service').map(resolveServiceSlug);
    const tierName = params.get('tier');
    const addons = params.getAll('addon');

    const matched = requested.filter((s) => AVAILABLE_SERVICES.includes(s));

    if (matched.length > 0) {
      setSelectedServices((prev) => {
        const merged = new Set([...prev, ...matched]);
        return Array.from(merged);
      });
    }

    if (tierName && matched.length > 0) {
      const service = getServiceBySlug(matched[0]);
      const tier = getTierByName(service, tierName);
      if (service && tier) {
        setSelectedTiers((prev) => ({ ...prev, [service.slug]: { service, tier } }));
      }
    }

    if (addons.length > 0) {
      setSelectedAddons((prev) => Array.from(new Set([...prev, ...addons])));
    }

    // Scroll the form into view if anything was prefilled
    if (matched.length > 0 || tierName || addons.length > 0) {
      requestAnimationFrame(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.search]);

  // Cal.com embed UI tweaks — keep theme matched to the page (light) and
  // hide event-type details we don't need on the embedded view.
  useEffect(() => {
    const uiConfig = {
      theme: 'light',
      cssVarsPerTheme: {
        light: { 'cal-brand': '#f97b22' },
        dark: { 'cal-brand': '#f97b22' },
      },
      hideEventTypeDetails: false,
      layout: 'month_view',
    };

    (async () => {
      try {
        // Desktop and mobile variants each mount their own namespaced embed,
        // so configure the UI for both.
        const [calDesktop, calMobile] = await Promise.all([
          getCalApi({ namespace: 'discovery-desktop' }),
          getCalApi({ namespace: 'discovery-mobile' }),
        ]);
        calDesktop('ui', uiConfig);
        calMobile('ui', uiConfig);
      } catch (err) {
        // Embed failed to load (offline / blocked) — silently degrade; the
        // contact form itself still works.
      }
    })();
  }, []);

  // Dev-only: React 18 StrictMode double-mounts the <Cal> embed, so the second
  // mount calls into an iframe the first mount already tore down. Cal's
  // cross-origin embed.js throws `iframe doesn't exist...` which CRA surfaces as
  // a generic "Script error." overlay. Booking still works; only the false
  // positive is noisy. Swallow exactly that error in development.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    const isCalEmbedNoise = (message, source) => {
      const msg = String(message || '');
      const src = String(source || '');
      return (
        src.includes('app.cal.com/embed/embed.js') ||
        msg.includes("iframe doesn't exist") ||
        (msg === 'Script error.' && src.includes('cal.com'))
      );
    };

    const onError = (event) => {
      if (isCalEmbedNoise(event?.message, event?.filename)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return true;
      }
      return undefined;
    };

    const onRejection = (event) => {
      const reason = event?.reason;
      const message = reason?.message || reason;
      if (isCalEmbedNoise(message, reason?.stack)) {
        event.preventDefault();
      }
    };

    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onRejection, true);

    return () => {
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onRejection, true);
    };
  }, []);

  // Drop addons that are no longer relevant after the user toggles services off.
  useEffect(() => {
    if (selectedAddons.length === 0) return;
    if (selectedAddons.every((label) => availableAddonLabels.has(label))) return;
    setSelectedAddons((prev) => prev.filter((label) => availableAddonLabels.has(label)));
  }, [availableAddonLabels, selectedAddons]);

  // Handle form submit — POSTs to Formspree via contactClient. Formspree
  // emails the form contents to the configured inbox and (when enabled in the
  // form's settings) sends an autoreply to the visitor. The client just ships
  // a clean structured payload; no hidden inputs needed.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsSuccess(false);

    const formData = new FormData(e.target);
    const get = (name) => (formData.get(name) || '').toString().trim();

    const addonsPayload = selectedAddons.map((label) => {
      let indicative = 'on quote';
      for (const slug of selectedServices) {
        const svc = getServiceBySlug(slug);
        const a = getAddonByLabel(svc, label);
        if (a) { indicative = a.indicative; break; }
      }
      return { label, indicative };
    });

    const payload = {
      _honey: get('_honey'),
      firstName: get('firstName'),
      lastName: get('lastName'),
      email: get('email'),
      company: get('company'),
      referralSource: get('referralSource'),
      budget: get('budget'),
      message: get('message'),
      services: selectedServices,
      addons: addonsPayload,
      package: selectedTierList
        .map(({ service, tier }) => `${service.title} — ${tier.name} (${tier.price}, ${tier.timeline})`)
        .join(' + '),
      contactPreference:
        contactMode === 'call'
          ? 'Discovery call (booked via Cal.com)'
          : 'Email reply only',
      acceptedTerms,
      estimate: hasEstimate
        ? {
            min: quoteEstimate.min,
            max: quoteEstimate.max,
            hasOpenItems: quoteEstimate.hasOpenItems,
          }
        : null,
      subject: dynamicSubject,
      quoteSummary: buildQuoteText(),
    };

    try {
      await postContact(payload);
      setIsSuccess(true);
      if (formRef.current) {
        formRef.current.reset();
        setFirstName('');
        setEmail('');
        setSelectedServices([]);
        setSelectedTiers({});
        setSelectedAddons([]);
        setAcceptedTerms(false);
        setHasMessage(false);
        setContactMode(null);
        formRef.current.querySelectorAll('textarea').forEach((ta) => {
          ta.style.height = '';
        });
      }
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Something went wrong while sending. Please try again or email info@skdesign.be directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleService = (service) => {
    setSelectedServices(prev =>
      prev.includes(service)
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
    // Drop any chosen package for a service that's being toggled off.
    removeTierForService(service);
  };

  const toggleAddon = (label) => {
    setSelectedAddons((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  // Builds a plaintext quote summary used both for the FormSubmit auto-response
  // (sent to the client) and for the hidden quoteSummary field (visible in the
  // mail Sander receives). Indicative ranges only — final quote follows manually.
  const buildQuoteText = () => {
    const lines = [];
    lines.push(`Hi ${firstName || 'there'},`);
    lines.push('');
    lines.push("Thanks for reaching out — here's a summary of what you requested:");
    lines.push('');
    if (selectedTierList.length > 0) {
      lines.push('- Packages:');
      selectedTierList.forEach(({ service, tier }) => {
        lines.push(`    + ${service.title} — ${tier.name} (${tier.price}, ${tier.timeline})`);
      });
    } else if (selectedServices.length > 0) {
      lines.push(`- Services: ${selectedServices.join(', ')}`);
    } else {
      lines.push('- Services: (to be discussed)');
    }
    if (selectedAddons.length > 0) {
      lines.push('- Add-ons:');
      selectedAddons.forEach((label) => {
        // Find the addon across any selected service that exposes it.
        let indicative = 'on quote';
        for (const slug of selectedServices) {
          const svc = getServiceBySlug(slug);
          const a = getAddonByLabel(svc, label);
          if (a) { indicative = a.indicative; break; }
        }
        lines.push(`    + ${label} (${indicative})`);
      });
    }
    lines.push('');
    lines.push("All amounts above are indicative and exclude 21% VAT. I'll come back within 24 working hours with a definitive quote tailored to your scope.");
    lines.push('');
    lines.push('— Sander Kuyken');
    return lines.join('\n');
  };

  const dynamicSubject = selectedTierList.length > 0
    ? `New quote request: ${selectedTierList.map(({ service, tier }) => `${service.title} — ${tier.name}`).join(' + ')}`
    : selectedServices.length > 0
      ? `New contact: ${selectedServices.join(', ')}`
      : 'New contact form submission';

  const quoteEstimate = useMemo(
    () =>
      computeQuoteEstimate({
        tiers: selectedTierList,
        services: selectedServices,
        addons: selectedAddons,
      }),
    [selectedTierList, selectedServices, selectedAddons]
  );

  const hasEstimate = quoteEstimate.min > 0 || quoteEstimate.max > 0;

  // Progress steps (Fase 7) — drives the subtle 4-step indicator above the form.
  const progressSteps = [
    { label: 'You', done: firstName.trim().length > 0 && email.trim().length > 0 },
    { label: 'Project', done: selectedServices.length > 0 || hasAnyTier },
    { label: 'Details', done: hasMessage },
    { label: 'Send', done: acceptedTerms && Boolean(contactMode) },
  ];

  useEffect(() => {
    const footerContainer = footerContainerRef.current;
    if (!footerContainer) return;

    // Set initial width to viewport width
    gsap.set(footerContainer, {
      width: '100vw',
      maxWidth: '100vw'
    });

    // Animate to normal width on scroll
    gsap.to(footerContainer, {
      width: '1400px',
      maxWidth: '1400px',
      ease: 'power2.out',
      scrollTrigger: {
        trigger: footerContainer,
        start: 'top 80%',
        end: 'top 20%',
        scrub: 1.5,
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.vars.trigger === footerContainer) {
          trigger.kill();
        }
      });
    };
  }, []);

  return (
    <>
    <SEO
      title="Contact"
      description="Got an idea or project? Get in touch with Sander Kuyken to discuss goals, scope and next steps."
      path="/contact"
    />
    <Navbar />
    <div className="bg-[#f2f3d9] px-5 py-16">
      <div className="w-full max-w-[1400px] mx-auto relative">
        {/* Contact Content */}
        <div className="px-4 sm:px-6 lg:px-7 py-12 lg:py-16">
          <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:gap-[50px]">
            {/* Left column */}
            <div className="flex flex-col gap-12 lg:gap-[71px] w-full lg:max-w-[661px]">
              <ContactHeader />

              {/* What happens next — sets expectations up front, on the left
                  next to the intro, so the visitor knows the path before they
                  fill in the form on the right. */}
              <WhatHappensNext />
            </div>
            {/* Right column */}
            <div className="flex flex-col w-full lg:max-w-[690px]">
              {/* Success Message */}
              {isSuccess && (
                <div className="mb-8 p-8 bg-[#f2f3d9] border-2 border-[#262624] rounded-[20px]">
                  <div className="font-bricolage font-extrabold text-[40px] sm:text-[48px] text-[#262624] uppercase tracking-[-2.4px] leading-[1.05] mb-4">
                    ✓ Message received
                  </div>
                  <div className="font-space-grotesk font-medium text-[18px] sm:text-[20px] text-[#262624] opacity-90 leading-[1.45] mb-4">
                    I'll get back to you within 24 working hours with a{' '}
                    <span className="font-bold">definitive, scoped quote</span> based on your request. A confirmation email with a summary is on its way to your inbox.
                  </div>
                  <div className="font-space text-[#262624]/60 text-[13px] sm:text-[14px] uppercase tracking-[0.18em]">
                    All prices excl. 21% VAT
                  </div>
                </div>
              )}
              
              <form ref={formRef} onSubmit={handleSubmit} method="POST" action={CONTACT_ENDPOINT}>
              {/* Progress indicator (Fase 7) */}
              <ContactProgress steps={progressSteps} />

              {/* Honeypot — bots fill every field, humans never see this */}
              <input
                type="text"
                name="_honey"
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              />

              {/* First & Last name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 w-full">
                <div className="flex flex-col">
                  <label htmlFor="firstName" className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-2px] sm:tracking-[-2.4px] lg:tracking-[-3.36px] leading-[0.9]">First name</label>
                  <div className="mt-5 sm:mt-6">
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none"
                      placeholder="John"
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <label htmlFor="lastName" className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-2px] sm:tracking-[-2.4px] lg:tracking-[-3.36px] leading-[0.9]">Last name</label>
                  <div className="mt-5 sm:mt-6">
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      autoComplete="family-name"
                      className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="mt-10">
                <label
                  htmlFor="email"
                  className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-1.8px] sm:tracking-[-2px] lg:tracking-[-2.24px] leading-[0.9] block"
                  style={{ background: 'unset', backgroundColor: 'unset' }}
                >
                  E-mail
                </label>
                <div className="mt-5 sm:mt-6">
                  <input
                    id="email"
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none"
                    placeholder="e-mail address"
                  />
                </div>
              </div>

              {/* Optional: Company / Brand + How did you find me? */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 w-full">
                <div className="flex flex-col">
                  <label htmlFor="company" className="font-bricolage font-extrabold uppercase text-[#262624] text-[22px] sm:text-[26px] lg:text-[32px] tracking-[-1px] sm:tracking-[-1.2px] lg:tracking-[-1.6px] leading-[0.95]">
                    Company / Brand
                    <span className="ml-2 font-space font-medium normal-case tracking-normal text-[12px] sm:text-[13px] text-[#262624]/50 align-middle">optional</span>
                  </label>
                  <div className="mt-3 sm:mt-4">
                    <input
                      id="company"
                      name="company"
                      type="text"
                      autoComplete="organization"
                      className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[16px] sm:text-[18px] lg:text-[20px] tracking-[-0.6px] sm:tracking-[-0.8px] pb-2 border-b border-[#262624]/60 outline-none focus:border-[#262624]"
                      placeholder="Acme Studio"
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <label htmlFor="referralSource" className="font-bricolage font-extrabold uppercase text-[#262624] text-[22px] sm:text-[26px] lg:text-[32px] tracking-[-1px] sm:tracking-[-1.2px] lg:tracking-[-1.6px] leading-[0.95]">
                    How did you find me?
                    <span className="ml-2 font-space font-medium normal-case tracking-normal text-[12px] sm:text-[13px] text-[#262624]/50 align-middle">optional</span>
                  </label>
                  <div className="mt-3 sm:mt-4 relative">
                    <select
                      id="referralSource"
                      name="referralSource"
                      defaultValue=""
                      className="sk-select w-full bg-transparent text-[#262624] font-space-grotesk font-bold text-[16px] sm:text-[18px] lg:text-[20px] tracking-[-0.6px] sm:tracking-[-0.8px] pb-2 pr-8 border-b border-[#262624] outline-none focus:border-[#262624] appearance-none cursor-pointer"
                    >
                      <option value="">Select an option…</option>
                      {REFERRAL_SOURCES.map((src) => (
                        <option key={src} value={src}>{src}</option>
                      ))}
                    </select>
                    <svg
                      className="pointer-events-none absolute right-1 bottom-3 w-4 h-4 text-[#262624]"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Budget — hidden when any tier package is selected (price is implicit) */}
              {!hasAnyTier && (
                <div className="mt-10">
                  <label
                    htmlFor="budget"
                    className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-1.8px] sm:tracking-[-2px] lg:tracking-[-2.24px] leading-[0.9] block"
                    style={{ background: 'unset', backgroundColor: 'unset' }}
                  >
                    Budget
                  </label>
                  <div className="mt-5 sm:mt-6">
                    <input
                      id="budget"
                      name="budget"
                      type="text"
                      className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none"
                      placeholder="€500 — €5.000"
                    />
                  </div>
                </div>
              )}

              {/* Service chips */}
              <div className="mt-12" role="group" aria-labelledby="service-group-label">
                <div id="service-group-label" className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-1.8px] sm:tracking-[-2px] lg:tracking-[-2.24px] leading-[0.9]">Service</div>
                <div className="mt-8 sm:mt-10 grid grid-cols-2 gap-4 sm:gap-5 w-full">
                  {AVAILABLE_SERVICES.map((t, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleService(t)}
                      className={`relative rounded-[62px] px-4 py-[12px] border uppercase font-bricolage font-extrabold text-[18px] sm:text-[20px] tracking-[-0.6px] leading-[1] transition-all duration-200 ${
                        selectedServices.includes(t)
                          ? 'bg-[#262624] text-white border-[#262624]'
                          : 'bg-transparent text-[#262624] border-[#262624] hover:bg-[#262624]/10'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                {/* Inline tier pickers — one per chip-selected service, so a
                    visitor can combine packages across services (e.g. Branding
                    + Web design). Picking one tier per service sums the prices
                    in the quote summary below. */}
                {selectedServices.map((slug) => {
                  const svc = getServiceBySlug(slug);
                  if (!svc?.tiers?.length) return null;
                  const chosen = selectedTiers[svc.slug]?.tier ?? null;
                  return (
                    <TierPickerInline
                      key={svc.slug}
                      service={svc}
                      selectedTierName={chosen?.name ?? null}
                      onSelect={(tier) => setTierForService(svc, tier)}
                      onClear={() => removeTierForService(svc.slug)}
                      showServiceLabel={selectedServices.length > 1}
                    />
                  );
                })}

                {/* Combine hint shown when 2+ services are picked. */}
                {selectedServices.length > 1 && (
                  <p className="mt-6 font-space text-[#262624]/65 text-[13px] sm:text-[14px] leading-snug">
                    Combining services? Pick one package per service above and I'll add the prices together below. Prefer a fully custom scope? Just describe it in the message field.
                  </p>
                )}
              </div>

              {/* Add-ons — grouped per selected service so a Branding +
                  Motion combo shows two labelled blocks instead of one flat
                  list. Each block falls back to the chip styling. */}
              {groupedAddons.length > 0 && (
                <div className="mt-12" role="group" aria-labelledby="addons-group-label">
                  <div id="addons-group-label" className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-1.8px] sm:tracking-[-2px] lg:tracking-[-2.24px] leading-[0.9]">
                    Add-ons
                  </div>
                  <p className="mt-3 sm:mt-4 font-space text-[#262624]/70 text-[14px] sm:text-[15px] leading-[1.45] max-w-[560px]">
                    Optional extras layered on top of your package — adjust the final quote. Toggle anything that fits.
                  </p>

                  <div className="mt-6 sm:mt-7 flex flex-col gap-7 sm:gap-8">
                    {groupedAddons.map((group) => {
                      const showHeader = groupedAddons.length > 1;
                      return (
                        <div key={group.slug} className="flex flex-col gap-3 sm:gap-3.5">
                          {showHeader && (
                            <div className="flex items-baseline gap-2">
                              <span className="text-base sm:text-lg leading-none">{group.service?.icon}</span>
                              <span className="font-space text-[#262624]/65 text-[11px] sm:text-xs uppercase tracking-[0.22em]">
                                {group.service?.title} add-ons
                              </span>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2.5 sm:gap-3">
                            {group.addons.map((addon) => {
                              const isOn = selectedAddons.includes(addon.label);
                              return (
                                <button
                                  key={`${group.slug}-${addon.label}`}
                                  type="button"
                                  onClick={() => toggleAddon(addon.label)}
                                  aria-pressed={isOn}
                                  className={`group inline-flex items-center gap-2.5 rounded-full border px-4 py-2.5 sm:px-5 sm:py-3 font-space font-medium text-sm sm:text-[15px] leading-tight transition-all duration-200 ${
                                    isOn
                                      ? 'bg-[#262624] text-[#e4dbd2] border-[#262624]'
                                      : 'bg-transparent text-[#262624] border-[#262624]/40 hover:border-[#262624]'
                                  }`}
                                >
                                  <span className="font-bricolage font-extrabold text-base sm:text-lg leading-none text-[#f97b22]">
                                    +
                                  </span>
                                  <span>{addon.label}</span>
                                  <span className={`text-xs sm:text-[13px] tabular-nums ${isOn ? 'text-[#e4dbd2]/70' : 'text-[#262624]/55'}`}>
                                    {addon.indicative}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Message */}
              <div className="mt-12">
                <label
                  htmlFor="message"
                  className="font-bricolage font-extrabold uppercase text-[#262624] text-[32px] sm:text-[40px] lg:text-[56px] tracking-[-1.8px] sm:tracking-[-2px] lg:tracking-[-2.24px] leading-[0.9] block"
                  style={{ background: 'unset', backgroundColor: 'unset' }}
                >
                  Message
                </label>
                <div className="mt-5 sm:mt-6">
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={1}
                    onInput={(e) => {
                      e.currentTarget.style.height = 'auto';
                      e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                      setHasMessage(e.currentTarget.value.trim().length > 0);
                    }}
                    className="w-full bg-transparent text-[#262624] placeholder:text-[#8c8c8c] font-space-grotesk font-bold text-[18px] sm:text-[20px] lg:text-[24px] tracking-[-0.8px] sm:tracking-[-1px] lg:tracking-[-1.2px] pb-2 border-b border-[#262624] outline-none resize-none overflow-hidden leading-[1.4] min-h-[2.4em]"
                    placeholder="Your message"
                  />
                </div>
              </div>

              {/* No hidden inputs needed — the JSON payload sent to
                  Formspree carries everything we need (services, package,
                  addons, contact preference, estimate, summary). Honeypot
                  stays. */}

              {/* Quote-breakdown banner — only shown when there's something to summarise */}
              {(hasAnyTier || selectedAddons.length > 0 || selectedServices.length > 0) && (
                <div className="mt-12 p-6 sm:p-7 lg:p-8 rounded-2xl bg-[#262624] text-[#f2f3d9] shadow-xl shadow-[#262624]/20">
                  <span className="font-space text-[#f2f3d9]/55 text-[10px] sm:text-xs uppercase tracking-[0.22em]">
                    {selectedTierList.length > 1 ? 'Selected packages' : 'Quote summary'}
                  </span>
                  {hasAnyTier ? (
                    <ul className="mt-3 space-y-3">
                      {selectedTierList.map(({ service, tier }) => (
                        <li key={service.slug} className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-bricolage font-extrabold text-xl sm:text-2xl lg:text-[28px] text-[#f2f3d9] leading-[1.1] tracking-tight">
                              {service.title} — {tier.name}
                            </div>
                            <div className="mt-1 font-space font-medium text-[14px] sm:text-base text-[#f2f3d9]/65">
                              {tier.price} · {tier.timeline}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeTierForService(service.slug)}
                            aria-label={`Remove ${service.title} package`}
                            className="flex-shrink-0 mt-1 inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#f2f3d9]/30 text-[#f2f3d9]/70 hover:text-[#262624] hover:bg-[#fe9043] hover:border-[#fe9043] transition-colors duration-200"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : selectedServices.length > 0 ? (
                    <div className="mt-2 font-bricolage font-extrabold text-xl sm:text-2xl text-[#f2f3d9] leading-[1.15] tracking-tight">
                      {selectedServices.join(' · ')}
                    </div>
                  ) : null}

                  {selectedAddons.length > 0 && (
                    <ul className="mt-5 space-y-2 pt-4 border-t border-[#f2f3d9]/15">
                      {selectedAddons.map((label) => {
                        let indicative = 'on quote';
                        for (const slug of selectedServices) {
                          const svc = getServiceBySlug(slug);
                          const a = getAddonByLabel(svc, label);
                          if (a) { indicative = a.indicative; break; }
                        }
                        return (
                          <li key={label} className="flex items-baseline gap-2 font-space text-[14px] sm:text-[15px] text-[#f2f3d9]/85">
                            <span className="text-[#fe9043] font-bricolage font-extrabold">+</span>
                            <span className="font-medium">{label}</span>
                            <span className="text-[#f2f3d9]/55 text-[12px] sm:text-[13px] ml-auto whitespace-nowrap">{indicative}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {hasEstimate && (
                    <div className="mt-6 pt-5 border-t border-[#f2f3d9]/15">
                      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-4">
                        <span className="font-space text-[#f2f3d9]/55 text-[10px] sm:text-xs uppercase tracking-[0.22em]">
                          Rough total estimate
                        </span>
                        <div className="font-bricolage font-extrabold text-3xl sm:text-4xl lg:text-[42px] leading-[1] tracking-tight text-[#fe9043]">
                          {quoteEstimate.min === quoteEstimate.max
                            ? formatEuro(quoteEstimate.min)
                            : `${formatEuro(quoteEstimate.min)} – ${formatEuro(quoteEstimate.max)}`}
                        </div>
                      </div>
                      {quoteEstimate.hasOpenItems && (
                        <p className="mt-2 font-space text-[#f2f3d9]/55 text-[12px] sm:text-[13px] leading-snug text-right">
                          + items on quote
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-5 pt-4 border-t border-[#f2f3d9]/15 font-space text-[#f2f3d9]/65 text-[13px] sm:text-sm leading-snug">
                    Final quote follows by email within 24 working hours · all prices excl. 21% VAT
                  </div>
                </div>
              )}

              {/* Contact preference — sits right by the submit zone and is
                  required: the visitor must pick email vs call before sending. */}
              <div className="mt-12">
                <ContactPreference mode={contactMode} onChange={setContactMode} variant="desktop" />
              </div>

              {/* Terms acceptance */}
              <div className="mt-10">
                <label htmlFor="acceptedTerms" className="flex items-start gap-3 sm:gap-4 cursor-pointer group select-none">
                  <span className="relative inline-flex flex-shrink-0 mt-0.5">
                    <input
                      id="acceptedTerms"
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="peer sr-only"
                      aria-describedby="acceptedTermsHelp"
                    />
                    <span
                      aria-hidden="true"
                      className="w-6 h-6 rounded-md border-2 border-[#262624] bg-transparent flex items-center justify-center transition-all duration-200 peer-checked:bg-[#262624] peer-focus-visible:ring-2 peer-focus-visible:ring-[#f97b22] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[#f2f3d9] group-hover:border-[#f97b22]"
                    >
                      <svg
                        className={`w-4 h-4 text-[#f2f3d9] transition-opacity duration-200 ${acceptedTerms ? 'opacity-100' : 'opacity-0'}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  </span>
                  <span id="acceptedTermsHelp" className="font-space text-[#262624] text-sm sm:text-[15px] leading-[1.45]">
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#f97b22] hover:text-[#262624] underline underline-offset-4 decoration-2 font-semibold transition-colors duration-300"
                    >
                      terms &amp; support conditions
                    </Link>
                    {' '}— covering pricing, payment, support window, revisions, ownership and cancellation.
                  </span>
                </label>
                <input type="hidden" name="acceptedTerms" value={acceptedTerms ? 'yes' : 'no'} />
              </div>

              {/* Submit */}
              <div className="mt-8">
                <button
                  type="submit"
                  disabled={isSubmitting || !acceptedTerms || !contactMode}
                  aria-disabled={isSubmitting || !acceptedTerms || !contactMode}
                  title={!contactMode ? 'Pick how I should get back to you' : !acceptedTerms ? 'Accept the terms to submit' : undefined}
                  className={`bg-gradient-to-l from-[#f97b22] from-[35.577%] to-[#fe9043] rounded-[62px] flex items-center gap-[12px] px-[22px] py-[12px] contact-btn relative overflow-hidden transition-transform duration-300 hover:scale-[1.03] cursor-pointer h-[66px] w-full lg:w-[266px] ${isSubmitting || !acceptedTerms || !contactMode ? 'opacity-50 cursor-not-allowed hover:scale-100' : ''}`}
                >
                  <span className="glare" aria-hidden="true"></span>
                  <div className="relative rounded-[62px] flex items-center justify-center flex-1">
                    <div className="font-bricolage font-normal text-[32px] sm:text-[36px] lg:text-[48px] text-[#e4dbd2] uppercase leading-[0.95]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                      {isSubmitting ? 'Sending...' : 'Submit'}
                    </div>
                  </div>
                  <div className="hidden lg:flex w-[48px] h-[48px] relative overflow-hidden rounded-full flex-shrink-0">
                    <svg className="block w-full h-full" fill="none" viewBox="0 0 165 164" preserveAspectRatio="none">
                      <g>
                        <rect width="165" height="164" rx="82" fill="#E4DBD2" />
                        <path className="arrow-path" d="M127.275 77.968L19.5 72.816V91.408L127.275 86.928L69.45 121.2V138L145.5 81.776L69.45 26V43.92L127.275 77.968Z" fill="#262624" />
                      </g>
                    </svg>
                  </div>
                </button>
              </div>
              </form>
            </div>
          </div>
        
        </div>
      </div>

      {/* Footer Section from ContactSection */}
      <div className="bg-[#f2f3d9] px-5 py-0">
        <div ref={footerContainerRef} className="mx-auto border-2 border-[#262624] relative">
          {/* The big "CONTACT" self-loop SVG was removed here — the visitor is
              already on /contact, so it only added noise. A little breathing
              room before the nav keeps the footer calm. */}
          <div className="pt-12 sm:pt-16" />

          {/* Navigation Menu */}
          <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-3 sm:gap-x-8 lg:gap-x-12 mb-10 sm:mb-12 lg:mb-16">
            {[{ label: 'HOME', path: '/' }, { label: 'work', path: '/works' }, { label: 'services', path: '/services' }, { label: 'about me', path: '/about' }, { label: 'insights', path: '/insights' }, { label: 'contact', path: '/contact' }].map((item, index) => (
              <Link key={index} to={item.path} className="font-bricolage font-extrabold text-[22px] sm:text-[30px] lg:text-[40px] text-[#262624] uppercase leading-[0.907] cursor-pointer hover:text-[#f97b22] transition-colors duration-300 text-center">
                {item.label}
              </Link>
            ))}
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left Message */}
            <div className="border-t-2 border-b-2 lg:border-2 border-[#262624] p-8 sm:p-10 lg:p-16 flex items-center justify-center">
              <div className="font-bricolage font-extrabold text-[20px] sm:text-[26px] lg:text-[30px] text-[#262624] uppercase leading-[0.907] text-center">
                Got some exciting ideas? Let's connect and create something extraordinary together!
              </div>
            </div>

            {/* Right Email Container */}
            <div className="border-b-2 lg:border-2 lg:border-l-0 border-[#262624] relative overflow-hidden flex items-center px-6 sm:px-8 lg:px-0 py-6 sm:py-8 lg:py-0">
              <div className="scrolling-email font-bricolage font-extrabold text-[32px] sm:text-[48px] lg:text-[64px] text-[#262624] uppercase leading-[0.907]">
                info@skdesign.be
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-b-2 lg:border-2 lg:border-t-0 border-[#262624] p-8 sm:p-12 lg:p-16">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              {/* Copyright */}
              <div className="font-bricolage font-light text-[56px] sm:text-[72px] lg:text-[96px] text-[#262624] uppercase leading-[0.907]">
                ©
              </div>

              {/* Social Links */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 w-full sm:w-auto">
                <a 
                  href="https://www.artstation.com/kuyken" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="border-2 border-[#262624] rounded-full px-4 py-3 cursor-pointer hover:bg-[#262624] hover:text-[#e4dbd2] transition-all duration-300 group text-center"
                >
                  <div className="font-bricolage font-light text-[20px] sm:text-[26px] lg:text-[32px] text-[#262624] uppercase leading-[0.907] group-hover:text-[#e4dbd2]">
                    artstation
                  </div>
                </a>
                <a 
                  href="https://www.linkedin.com/in/sander-kuyken-8a384716b/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="border-2 border-[#262624] rounded-full px-4 py-3 cursor-pointer hover:bg-[#262624] hover:text-[#e4dbd2] transition-all duration-300 group text-center"
                >
                  <div className="font-bricolage font-light text-[20px] sm:text-[26px] lg:text-[32px] text-[#262624] uppercase leading-[0.907] group-hover:text-[#e4dbd2]">
                    Linkedin
                  </div>
                </a>
              </div>

              {/* Name */}
              <div className="font-bricolage font-light text-[28px] sm:text-[34px] lg:text-[40px] text-[#262624] leading-[0.907]">
                Sander Kuyken
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Transition(ContactSection);
